import type { OptionType } from '@/app/components/form/MultiSearchInput/types';
import type { ResponseType } from '@/types/apiTypes';
import type { ClientCrmStatus, Nullable } from '@/types/common';
import type { ClientDocument } from './clientDetailsApiTypes';

export interface PassportDataWithName extends PassportData {
  document_name: string;
}

export interface PassportData {
  passport_number: string;
  issuance_country: string;
  issue_date: string;
  expiry_date: string;
}
export type ClientDocumentWithPassportData = {
  id: number;
  client_id: Nullable<number>;
  document_name: string;
  document_type: 'passport' | 'id';
  document_url: Nullable<string>;
  passport_number: Nullable<string>;
  expiry_date: Nullable<string>;
  issuance_country: Nullable<string>;
  issue_date: Nullable<string>;
  created_at: Nullable<string>;
  updated_at: Nullable<string>;
};
export type ClientDocumentPayload = {
  document_type: 'passport' | 'id';
  document_name: string;
  passport_number: string;
  expiry_date: string;
  issue_date: string;
  issuance_country: string;
};
export type ClientDocumentResponse = {
  id: number;
  client_id: number;
  document_type: 'passport' | 'id';
  document_name: string;
  document_url: string;
  passport_number: string;
  expiry_date: string;
  issuance_country: string;
  issue_date: string;
  created_at: string;
  updated_at: string;
};
export const SEAT_PREFERENCES = {
  window: 'Window',
  aisle: 'Aisle',
} as const;
export const FLIGHT_PREFERENCES = {
  commercial: 'commercial',
  private: 'private',
} as const;
export const RELATIONSHIP_TYPES = {
  spouse_partner: 'spouse_partner',
  child: 'child',
  parent: 'parent',
  sibling: 'sibling',
  other_family_member: 'other_family_member',
  friend_colleague: 'friend_colleague',
} as const;
export type RelationshipType = (typeof RELATIONSHIP_TYPES)[keyof typeof RELATIONSHIP_TYPES];

export const RELATIONSHIP_LABELS: Record<RelationshipType, string> = {
  spouse_partner: 'Spouse/Partner',
  child: 'Child',
  parent: 'Parent',
  sibling: 'Sibling',
  other_family_member: 'Other Family Member',
  friend_colleague: 'Friend/Colleague',
};

/** Converts a raw relationship value (e.g. "friend_colleague") to a human-readable label. */
export const formatRelationship = (value: string | null | undefined): string => {
  if (!value) return '';
  return RELATIONSHIP_LABELS[value as RelationshipType] ?? value;
};
export type MeasurementSystem = 'us' | 'metric';
export type SeatPreference = (typeof SEAT_PREFERENCES)[keyof typeof SEAT_PREFERENCES];
export type FlightPreference = (typeof FLIGHT_PREFERENCES)[keyof typeof FLIGHT_PREFERENCES];
export type ClassPreference = (typeof CLASS_PREFERENCE_OPTIONS)[number]['key'];
export type RelatedPassengerBasePath = `related_passengers.${number}`;

export type RelatedPassengerCreatePayload = {
  id: number;
  identity: Nullable<string>;
  first_name: string;
  last_name: string;
  email: Nullable<string>;
  phone?: Nullable<string>;
  phones?: ClientPhonesType[];
  date_of_birth: Nullable<string>;
  profile_picture_url: Nullable<string>;
  is_vip: boolean;
  is_draft: boolean;
  draft_identification: Nullable<string>;
  status: ClientCrmStatus;
  full_name: string;
  agent: Nullable<{
    id: number;
    name: string;
  }>;
};

export type ClientComfortDetailsData = {
  personal_metric_system: MeasurementSystem;
  comfort_related: {
    us: {
      height: Nullable<string>;
      weight: Nullable<string>;
    };
    metric: {
      height: Nullable<string>;
      weight: Nullable<string>;
    };
  };
  information: Nullable<string>;
};

export type RelatedPassengerClientData = Pick<
  ClientInfoType,
  | 'client_id'
  | 'userHasCertifiedTheInformation'
  | 'preferred_name'
  | 'first_name'
  | 'middle_name'
  | 'last_name'
  | 'gender'
  | 'email'
  | 'phone'
  | 'client_mailing_address_line_2'
> & {
  additional_emails: ClientEmailsType[];
  phones: ClientPhonesType[];
  crm_emails?: ClientEmailsType[];
  crm_phones?: ClientPhonesType[];
  date_of_birth: Nullable<string>;
  relationship: Nullable<RelationshipType | string>;
  contact_primary_client_id: Nullable<number>;
  same_as_primary: boolean;
  client_mailing_address: Nullable<string>;
  client_mailing_city: Nullable<string>;
  client_mailing_state: Nullable<string>;
  client_mailing_country: Nullable<string>;
  zip_code: Nullable<string>;
};

export type RelatedPassengerPreferencesData = {
  allergies: Nullable<string>;
  fly: {
    seat: Nullable<SeatPreference>;
    allergies: Nullable<string>;
    preferred_private_aviation_company: Nullable<string>;
    fly_private_aviation: Nullable<'yes' | 'no'>;
    class_preference: Nullable<ClientPreferencesOptionType[]>;
  } | null;
  personal: ClientComfortDetailsData;
  diet: string[];
  general_interests?: ClientPreferencesOptionType[];
  additional_notes?: Nullable<string>;
};

export type RelatedPassengerPreferencesFormData = {
  allergies: Nullable<string>;
  fly: {
    seat: Nullable<SeatPreference>;
    allergies: Nullable<string>;
    preferred_private_aviation_company: Nullable<string>;
    fly_private_aviation: Nullable<'yes' | 'no'>;
    class_preference: Nullable<ClassPreference[]>;
  } | null;
  personal: ClientComfortDetailsData;
};

export type RelatedPassengerFormData = {
  client: RelatedPassengerClientData;
  tsa: TsaType;
  passport: PassportFormType;
  preference_data: RelatedPassengerPreferencesFormData;
  documents: ClientDocumentWithPassportData[];
  loyalty_numbers: LoyaltyProgramFormData[];
};

export interface FrequentFlyersOption {
  id: number;
  name: string;
}

export type PrimaryFrequentFlyers = {
  frequent_flyer: string;
  frequent_flyer_id: number | null;
  program_number: string;
  ff_id: number | null;
};

export type SourceData = {
  id: number;
  title: string;
};
export type EmergencyRelationshipData = {
  id: number;
  description: string;
};

// Interface for backend response format
export interface ClientPreferencesOptionType {
  value: string;
  translated_value: string;
}

export const RelationshipOptions = [
  {
    value: RELATIONSHIP_TYPES.spouse_partner,
    label: 'Spouse/Partner',
  },
  {
    value: RELATIONSHIP_TYPES.child,
    label: 'Child',
  },
  {
    value: RELATIONSHIP_TYPES.parent,
    label: 'Parent',
  },
  {
    value: RELATIONSHIP_TYPES.sibling,
    label: 'Sibling',
  },
  {
    value: RELATIONSHIP_TYPES.other_family_member,
    label: 'Other family member',
  },
  {
    value: RELATIONSHIP_TYPES.friend_colleague,
    label: 'Friend/Colleague',
  },
];
export type RelationshipOption = (typeof RelationshipOptions)[keyof typeof RelationshipOptions];

export type RelatedTravelerLinkRelationship = {
  value: RelationshipType;
  label: string;
};

export type RelatedTravelerLinkRecord = {
  uuid: string;
  primary_traveler_id: number;
  primary_traveler: { full_name: string; email: Nullable<string> };
  related_traveler_id: number;
  related_traveler: { full_name: string; email: Nullable<string> };
  relationship: RelatedTravelerLinkRelationship;
  created_at: string;
};

export type CreateRelatedTravelerLinkBody = {
  primary_traveler_id: number;
  related_traveler_id: number;
  relationship: RelationshipType;
};

export type CreateRelatedTravelerLinkResponse = ResponseType<RelatedTravelerLinkRecord>;

export const CLASS_PREFERENCE_OPTIONS = [
  { key: 'first_class', label: 'First Class' },
  { key: 'business_class', label: 'Business Class' },
  { key: 'premium_economy_class', label: 'Premium Economy Class' },
  { key: 'economy_class', label: 'Economy Class' },
  { key: 'comfort_class', label: 'Comfort Class' },
  { key: 'economy_plus', label: 'Economy Plus' },
] as const;

export const ACCOMMODATION_PREFERENCE_OPTIONS = [
  { key: 'boutique_hotels', label: 'Boutique Hotels' },
  { key: 'design_forward_hotels', label: 'Design-Focused Hotels' },
  { key: 'independently_owned_hotels', label: 'Independently-Owned Hotels' },
  { key: 'sustainable_focused_hotels', label: 'Eco-Hotels' },
  {
    key: 'global_brand_hotels',
    label: 'Global Brands (ex. Four Seasons, Marriott, etc.)',
  },
  { key: 'yacht', label: 'Yacht' },
  { key: 'megaship_cruises', label: 'Megaship Cruises' },
  { key: 'expedition_cruises', label: 'Expedition Cruises' },
  { key: 'river_cruises', label: 'River Cruises' },
] as const;

export const DRINKING_PREFERENCE_OPTIONS = [
  { key: 'red_wine', label: 'Red Wine' },
  { key: 'white_wine', label: 'White Wine' },
  { key: 'rose', label: 'Rosé' },
  { key: 'sparkling_champagne', label: 'Sparkling/Champagne' },
  { key: 'cocktails', label: 'Cocktails' },
  { key: 'non_alcoholic_beverages', label: 'Non-Alcoholic Beverages' },
  { key: 'sparkling_water', label: 'Sparkling Water' },
  { key: 'flat_water', label: 'Flat Water' },
] as const;

export const PILLOW_PREFERENCE_OPTIONS = [
  { key: 'foam_pillows', label: 'Foam Pillows' },
  { key: 'feather_pillows', label: 'Feather Pillows' },
  { key: 'down_pillows', label: 'Down Pillows' },
  { key: 'down_alternative_pillows', label: 'Down Alternative Pillows' },
] as const;

export const ROOM_PREFERENCE_OPTIONS = [
  { key: 'king_beds', label: 'King Beds' },
  { key: 'queen_beds', label: 'Queen Beds' },
  { key: 'double_beds', label: 'Double Beds' },
  { key: 'standard_room', label: 'Standard Room' },
  { key: 'conjoining_rooms', label: 'Conjoining Rooms' },
] as const;

export const SPA_PREFERENCE_OPTIONS = [
  { key: 'female_therapist', label: 'Female Therapist' },
  { key: 'male_therapist', label: 'Male Therapist' },
  { key: 'no_preference', label: 'No Preference' },
] as const;

export const INTEREST_PREFERENCE_OPTIONS = [
  { key: 'adventure', label: 'Adventure' },
  { key: 'architecture', label: 'Architecture' },
  { key: 'art_culture', label: 'Art & Culture' },
  { key: 'curated_group_travel', label: 'Curated Group Travel' },
  { key: 'fashion_shopping', label: 'Fashion / Shopping' },
  { key: 'gastronomy', label: 'Gastronomy' },
  { key: 'history', label: 'History' },
  { key: 'interior_design', label: 'Interior Design' },
  { key: 'private_jet_expeditions', label: 'Private Jet Expeditions' },
  { key: 'safari', label: 'Safari' },
  { key: 'ski_snowboard', label: 'Ski / Snowboard' },
  { key: 'spa_wellness', label: 'Spa & Wellness' },
  { key: 'sporting_events', label: 'Sporting Events' },
  { key: 'sustainability', label: 'Sustainability' },
  { key: 'transformative_fitness_program', label: 'Transformative Fitness Program' },
  { key: 'wine_spirit', label: 'Wine & Spirit' },
] as const;

export const DIET_PREFERENCE_OPTIONS = [
  { key: 'vegan', label: 'Vegan' },
  { key: 'vegetarian', label: 'Vegetarian' },
  { key: 'kosher', label: 'Kosher' },
  { key: 'diabetic', label: 'Diabetic' },
  { key: 'low_sodium', label: 'Low Sodium' },
  { key: 'pescatarian', label: 'Pescatarian' },
  { key: 'gluten_free', label: 'Gluten-Free' },
  { key: 'health_conscious', label: 'Health Conscious' },
  { key: 'low_carb', label: 'Low Carb' },
  { key: 'paleo', label: 'Paleo' },
] as const;

export const ALLERGY_PREFERENCE_OPTIONS = [
  { key: 'eggs', label: 'Eggs' },
  { key: 'peanuts', label: 'Peanuts' },
  { key: 'dairy', label: 'Dairy' },
  { key: 'soy', label: 'Soy' },
  { key: 'fish', label: 'Fish' },
  { key: 'shellfish', label: 'Shellfish' },
  { key: 'celiac', label: 'Celiac' },
] as const;

export const RENTAL_CAR_PREFERENCE_OPTIONS = [
  { key: 'economy', label: 'Economy' },
  { key: 'compact', label: 'Compact' },
  { key: 'intermediate', label: 'Intermediate' },
  { key: 'full_size', label: 'Full Size' },
  { key: 'premium_car', label: 'Premium Car' },
  { key: 'intermediate_suv', label: 'Intermediate SUV' },
  { key: 'mini_van', label: 'Mini Van' },
  { key: 'convertible', label: 'Convertible' },
  { key: 'full_size_suv', label: 'Full Size SUV' },
] as const;

export const AMENITIES_PREFERENCE_OPTIONS = [
  { key: 'fresh_fruit', label: 'Fresh Fruit' },
  { key: 'fresh_flowers', label: 'Fresh Flowers' },
  { key: 'sweet_snacks', label: 'Sweet snacks' },
  { key: 'salty_snacks', label: 'Salty snacks' },
  { key: 'wine', label: 'Wine' },
  { key: 'water', label: 'Water' },
] as const;

export type AccommodationPreference = (typeof ACCOMMODATION_PREFERENCE_OPTIONS)[number]['key'];
export type DrinkingPreference = (typeof DRINKING_PREFERENCE_OPTIONS)[number]['key'];
export type PillowPreference = (typeof PILLOW_PREFERENCE_OPTIONS)[number]['key'];
export type RoomPreference = (typeof ROOM_PREFERENCE_OPTIONS)[number]['key'];
export type SpaPreference = (typeof SPA_PREFERENCE_OPTIONS)[number]['key'];
export type InterestPreference = (typeof INTEREST_PREFERENCE_OPTIONS)[number]['key'];
export type DietOption = (typeof DIET_PREFERENCE_OPTIONS)[number]['key'];
export type AllergyOption = (typeof ALLERGY_PREFERENCE_OPTIONS)[number]['key'];
export type RentalCarOption = (typeof RENTAL_CAR_PREFERENCE_OPTIONS)[number]['key'];
export type AmenitiesOption = (typeof AMENITIES_PREFERENCE_OPTIONS)[number]['key'];

export type ClientPreferenceData = {
  allergies?: string | string[];
  dietary_restrictions?: string;

  fly?: {
    fly_private_aviation?: Nullable<'yes' | 'no'>;
    preferred_private_aviation_company?: string | null;
    seat: 'Window' | 'Aisle';
    class_preference: string[];
    preference_note?: string; // Legacy field
  };

  travel?: string;

  stay?:
    | {
        /** Standard keys and/or legacy free-text rows from CRM (API allows string max 255). */
        accommodation_types?: (AccommodationPreference | string)[];
        preferences_note?: string;
      }
    | string[];

  rental_car_preferences?: string[];

  diet?: string[] | { others?: string[] } | Record<string, string | string[]>;
  rental_car?: string[] | { service?: string; company_name?: string; loyalty_number?: string };

  amenities_own?: string[];
  amenities_others?: string[];

  general_interests?: InterestPreference[];
  beverage_preferences?: DrinkingPreference[];
  pillow_preferences?: PillowPreference[];
  room_configuration_preferences?: RoomPreference[];
  spa_preferences?: SpaPreference[];
  spa_preferences_special_requests?: string;
  likes?: string;
  dislikes?: string;

  personal?: {
    comfort_related?: {
      us?: {
        height?: string;
        weight?: string;
      };
      metric?: {
        height?: string;
        weight?: string;
      };
    };
    information?: string;
  };
};

export type ClientPreferencesType = {
  allergies: Nullable<string>;

  fly: {
    preferred_private_aviation_company: Nullable<string>;
    fly_private_aviation: Nullable<'yes' | 'no'>;
    seat: 'Window' | 'Aisle';
    class_preference: Nullable<ClassPreference[]>;
  } | null;

  travel: Nullable<string>;

  stay: {
    /** Standard keys and/or legacy free-text rows (CRM / API allows mixed payloads). */
    accommodation_types: Nullable<(AccommodationPreference | string)[]>;
    /** CRM legacy bucket; merged into accommodation_types when hydrating forms. */
    others?: Nullable<string[]>;
    preferences_note: Nullable<string>;
  };

  general_interests: Nullable<InterestPreference[]>;
  beverage_preferences: Nullable<DrinkingPreference[]>;
  pillow_preferences: Nullable<PillowPreference[]>;
  room_configuration_preferences: Nullable<RoomPreference[]>;
  spa_preferences: Nullable<SpaPreference[]>;
  spa_preferences_special_requests: Nullable<string>;
  likes: Nullable<string>;
  dislikes: Nullable<string>;

  personal: {
    personal_metric_system: MeasurementSystem;
    information: Nullable<string>;
    comfort_related: {
      us: {
        height: Nullable<string>;
        weight: Nullable<string>;
      };
      metric: {
        height: Nullable<string>;
        weight: Nullable<string>;
      };
    };
  };
};

export type ClientEmailsType = {
  email: string;
  uuid?: string | null;
  title?: string | null;
  is_primary?: boolean;
};
export type ClientPhonesType = {
  phone: string;
  title: Nullable<string>;
  uuid?: string | null;
  raw_phone?: string | null;
  is_primary?: boolean;
};

type EditClientPayload = {
  id: number;
  relationship: Nullable<RelationshipType>;
  is_billing_same_as_mailing: boolean;
  status: ClientCrmStatus;
  client_mailing_address: Nullable<string>;
  client_mailing_address_line_2: Nullable<string>;
  client_mailing_city: Nullable<string>;
  client_mailing_state: Nullable<string>;
  client_mailing_country: Nullable<string>;
  zip_code: Nullable<string>;
  client_billing_address: Nullable<string>;
  client_billing_address_line_2: Nullable<string>;
  client_billing_city: Nullable<string>;
  client_billing_state: Nullable<string>;
  client_billing_country: Nullable<string>;
  billing_zip_code: Nullable<string>;
  profile_picture_url: Nullable<string>;
  email: Nullable<string>;
  additional_emails: ClientEmailsType[];
  emails?: {
    action: 'INSERT' | 'UPDATE' | 'DELETE';
    uuid?: string;
    title?: string;
    email?: string;
    is_primary?: boolean;
  }[];
  date_of_birth: Nullable<string>;
  phone: Nullable<string>;
  contact_primary_client_id: Nullable<number>;
  phones: Array<
    | ClientPhonesType
    | {
        action: 'INSERT' | 'UPDATE' | 'DELETE';
        uuid?: string;
        title?: string;
        raw_phone?: string;
        is_primary?: boolean;
      }
  >;
  preferred_airport_ids: number[];
  preferred_name: Nullable<string>;
  userHasCertifiedTheInformation: boolean;
  profile_picture_storage_id: Nullable<string>;
  emergency_contact_name: Nullable<string>;
  emergency_relationship_type_id: Nullable<number>;
  emergency_relationship: Nullable<OptionType<number, string>>;
  emergency_contact_phone: Nullable<string>;
  emergency_contact_email: Nullable<string>;
  profile_picture_gallery_id: Nullable<number>;
  first_name: Nullable<string>;
  middle_name: Nullable<string>;
  last_name: Nullable<string>;
  is_vip: boolean;
  gender: Nullable<string>;
  source_id: Nullable<number>;
  notifyAgent: 1 | 0;
  password: Nullable<string>;
};

type GetPassengerTsaType = {
  id: number;
  client_id: number;
  clear_member: null;
  global_entry: string;
  global_expiry_date: string;
  known_traveler_number: string;
  known_traveler_number_expire_date: string;
  membership_expiry_date: null;
  created_at: string;
  updated_at: string;
};

export interface GetRelatedPassengersData extends Omit<GetClientData, 'preferences'> {
  id: number;
  relationship: Nullable<ClientPreferencesOptionType>;
  is_primary: boolean;
  is_primary_passenger?: boolean;
  is_minor?: boolean;
  root_traveler_id?: Nullable<number>;
  preferences?: {
    id: number;
    client_id: number;
    preference_data: RelatedPassengerPreferencesData;
    likes: Nullable<string>;
    dislikes: Nullable<string>;
    created_at: string;
    updated_at: string;
  };
}

export type ClientInfoType = {
  client_id: number;
  userHasCertifiedTheInformation: boolean;
  preferred_name: Nullable<string>;
  first_name: string;
  middle_name: Nullable<string>;
  last_name: string;
  is_vip: boolean;
  gender?: Nullable<string>;
  source_id: number;
  email: string;
  additional_emails: ClientEmailsType[];
  crm_emails?: ClientEmailsType[];
  date_of_birth?: Nullable<string>;
  phone?: Nullable<string>;
  phones: ClientPhonesType[];
  crm_phones?: ClientPhonesType[];
  client_mailing_address?: Nullable<string>;
  client_mailing_address_line_2?: Nullable<string>;
  client_mailing_city?: Nullable<string>;
  client_mailing_state?: Nullable<string>;
  client_mailing_country?: Nullable<string>;
  is_billing_same_as_mailing?: boolean;
  client_billing_address?: Nullable<string>;
  client_billing_address_line_2?: Nullable<string>;
  client_billing_city?: Nullable<string>;
  client_billing_state?: Nullable<string>;
  client_billing_country?: Nullable<string>;
  billing_zip_code?: Nullable<string>;
  profile_picture_url?: Nullable<string>;
  zip_code?: Nullable<string>;
  social_facebook_url: Nullable<string>;
  social_instagram_url: Nullable<string>;
  social_linkedin_url: Nullable<string>;
  social_twitter_url: Nullable<string>;
  social_custom_url: Nullable<string>;
  preferred_airport_ids: number[];
  profile_picture_storage_id: Nullable<string>;
  profile_picture_gallery_id?: Nullable<number>;
  emergency_contact_name?: Nullable<string>;
  emergency_relationship_type_id?: Nullable<number>;
  relationship?: Nullable<RelationshipType>;
  emergency_relationship: Nullable<OptionType<number, string>>;
  emergency_contact_phone?: Nullable<string>;
  emergency_contact_email?: Nullable<string>;
};

type TsaType = {
  known_traveler_number: Nullable<string>;
  known_traveler_number_expire_date: Nullable<string>;
  global_entry: Nullable<string>;
  global_expiry_date: Nullable<string>;
};

export type PassportFormType = {
  passport_number: Nullable<string>;
  expiry_date: Nullable<string>;
  passport_id: Nullable<number>;
};

type EmergencyContactType = {
  name: string;
  relationship_type_id: number;
  phone: string;
  email: string;
};

type BillingAddressType = {
  address: Nullable<string>;
  address_line_2: Nullable<string>;
  city: Nullable<string>;
  state: Nullable<string>;
  country: Nullable<string>;
  zip_code: Nullable<string>;
  same_as_mailing: boolean;
};

type PreferredAirportsType = {
  city: string;
  country: string;
  code: string;
  id: number;
};

type RequestPreferredAirportsType = {
  airport_ids: number[];
};

export interface LoyaltyProgramFormData extends AddLoyaltyNumbersCategoryPayload {
  lp_id: Nullable<number>;
  is_legacy: boolean;
}

export type CreateClientFormData = {
  primary_passenger: {
    client: ClientInfoType;
    preference_data: ClientPreferencesType;
    tsa: TsaType;
    passport: PassportFormType;
    documents: ClientDocumentWithPassportData[];
    selected_airports: OptionType<number, string>[];
    loyalty_numbers: LoyaltyProgramFormData[];
  };

  related_passengers: RelatedPassengerFormData[];
};

export type EditClientData = {
  client: Partial<EditClientPayload>;
  preferences: Partial<ClientPreferencesType>;
  passport: Partial<PassportFormType>;
  tsa: Partial<TsaType>;
  documents: Partial<ClientDocumentWithPassportData>[];
  emergency_contact: Partial<EmergencyContactType>;
  billing_address: Partial<BillingAddressType>;
  preferred_airports: Partial<RequestPreferredAirportsType>;
  loyalty_numbers: Partial<UpdateLoyaltyNumbersCategoryPayload>[];
  metadata: Record<string, string | null>;
};

export type CreateClientData = {
  id: number;
  identity: Nullable<string>;
  first_name: string;
  last_name: string;
  email?: Nullable<string>;
  emails?: ClientEmailsType[];
  phone?: Nullable<string>;
  date_of_birth: Nullable<string>;
  profile_picture_url: Nullable<string>;
  is_vip: boolean;
  is_draft: boolean;
  draft_identification: Nullable<string>;
  status: ClientCrmStatus;
  full_name: string;
  agent: Nullable<string>;
};

export type GetClientPreferencesData = {
  allergies: Nullable<string>;
  general_interests?: ClientPreferencesOptionType[];
  pillow_preferences?: ClientPreferencesOptionType[];
  drinking_preferences?: ClientPreferencesOptionType[];
  beverage_preferences: ClientPreferencesOptionType[];
  room_configuration_preferences?: ClientPreferencesOptionType[];
  spa_preferences_special_requests: Nullable<string>;
  amenities_traveling_other: string[];
  spa_preferences?: ClientPreferencesOptionType[];
  pillows: string[];
  wine: string[];
  travel: Nullable<string>;
  likes?: string;
  dislikes?: string;
  stay: {
    accommodation_types?: AccommodationPreference[];
    /** CRM legacy free-text rows (merged into modal/UI with accommodation_types). */
    others?: string[];
    preferences_note?: string;
  };
  rental_car?: string;
  rental_car_preferences?: string[];
  interests?: ClientPreferencesOptionType[];
  diet?: string[] | { others?: unknown[] } | null;
  amenities_others?: string[];
  amenities_own?: string[];
  fly: {
    preferred_private_aviation_company: Nullable<string>;
    fly_private_aviation: Nullable<'yes' | 'no'>;
    seat: SeatPreference;
    class_preference: ClientPreferencesOptionType[];
  };
  tsa: {
    global_expiry_date: Nullable<string>;
  };
  personal: {
    personal_metric_system?: MeasurementSystem;
    information: Nullable<string>;
    comfort_related: {
      us: {
        height: Nullable<string>;
        weight: Nullable<string>;
      };
      metric: {
        height: Nullable<string>;
        weight: Nullable<string>;
      };
      information: Nullable<string>;
    };
  };
};

export type LoyaltyNumbersData = {
  id: number;
  client_id: number;
  loyalty_category_id: number;
  number: string;
  loyalty_program: Nullable<string>;
  frequent_flyer_number: Nullable<string>;
  is_legacy: boolean;
  note: Nullable<string>;
  category: Nullable<{
    id: number;
    title: string;
    short_code: string;
    type: string;
    alliance: string;
  }>;
  created_at: string;
  updated_at: string;
};

export type GetClientData = {
  id: number;
  userHasCertifiedTheInformation: boolean;
  preferred_name: Nullable<string>;
  first_name: Nullable<string>;
  middle_name: Nullable<string>;
  last_name: Nullable<string>;
  full_name: Nullable<string>;
  is_primary: boolean;
  is_vip: boolean;
  is_draft: boolean;
  draft_identification: Nullable<string>;
  is_billing_same_as_mailing: boolean;
  gender: Nullable<string>;
  source: Nullable<SourceData>;
  email?: Nullable<string>;
  additional_emails?: ClientEmailsType[];
  emails?: ClientEmailsType[];
  date_of_birth: Nullable<string>;
  phone?: Nullable<string>;
  contact_primary_client_id: Nullable<number>;
  client_mailing_address: Nullable<string>;
  client_mailing_address_line_2: Nullable<string>;
  client_mailing_city: Nullable<string>;
  client_mailing_state: Nullable<string>;
  client_mailing_country: Nullable<string>;
  zip_code: Nullable<string>;
  client_billing_address: Nullable<string>;
  client_billing_address_line_2: Nullable<string>;
  client_billing_city: Nullable<string>;
  client_billing_state: Nullable<string>;
  client_billing_country: string;
  billing_zip_code: Nullable<string>;
  emergency_contact_name: string;
  emergency_relationship: Nullable<EmergencyRelationshipData>;
  emergency_contact_phone: string;
  emergency_contact_email: Nullable<string>;
  social_facebook_url: string;
  social_instagram_url: Nullable<string>;
  social_linkedin_url: Nullable<string>;
  social_twitter_url: string;
  social_custom_url: Nullable<string>;
  preferred_airports: PreferredAirportsType[];
  phones: ClientPhonesType[];
  profile_picture_url: Nullable<string>;
  documents: ClientDocument[];
  tsa?: GetPassengerTsaType;
  loyalty_numbers?: LoyaltyNumbersData[];
  preferences?: {
    id: number;
    client_id: number;
    preference_data: GetClientPreferencesData;
    likes: Nullable<string>;
    dislikes: Nullable<string>;
    created_at: string;
    updated_at: string;
  };
  relationship: Nullable<ClientPreferencesOptionType>;
};

export interface EditClientResponseData {
  client: {
    profile_picture_url: string;
  };
  billing_address: {
    billing_address_line_1: string;
    billing_address_line_2: string;
    billing_city: string;
    billing_state: string;
    billing_country: string;
    billing_zip_code: string;
    client_id: number;
  };
  loyalty_numbers: LoyaltyNumbersData[];
}

export type LoyaltyNumbersCategory = {
  id: number;
  title: string;
  short_code: string;
  created_by_id: number | null;
  updated_by_id: number | null;
  created_at: string;
  updated_at: string;
  order: number;
  type: string;
  alliance: string;
  frequent_flyer_number: string;
};

export type AddLoyaltyNumbersCategoryPayload = {
  category_id: Nullable<number>;
  number: Nullable<string>;
  loyalty_program: Nullable<string>;
  frequent_flyer_number: Nullable<string>;
  note: Nullable<string>;
};

export type UpdateLoyaltyNumbersCategoryPayload = {
  loyalty_numbers: {
    category_id: number;
    number: Nullable<string>;
    loyalty_program: Nullable<string>;
    frequent_flyer_number: Nullable<string>;
    note: Nullable<string>;
  }[];
};

export type PrimaryOrRelatedPassengerType = GetClientData | GetRelatedPassengersData;
export type EditClientResponse = ResponseType<EditClientResponseData>;
export type GetClientResponse = ResponseType<GetClientData>;
export type CreateClientResponse = ResponseType<CreateClientData>;
export type GetClientDocumentsResponse = ResponseType<ClientDocumentWithPassportData[]>;
export type CreateRelatedPassengerResponse = ResponseType<RelatedPassengerCreatePayload>;
export type GetRelatedPassengersResponse = ResponseType<GetRelatedPassengersData[]>;
export type GetLoyaltyNumbersCategoriesResponse = ResponseType<LoyaltyNumbersCategory[]>;

// Grouped passengers view types
export interface GroupedBasicInformationItem {
  id: number;
  firstName: string;
  middleName: Nullable<string>;
  lastName: string;
  gender: string | null;
  dateOfBirth: string | null;
  age: number | null;
  relationshipToPrimary: string | null;
  isPrimary: boolean;
}

export interface GroupedContactInformationItem {
  id: number;
  fullName: string;
  email: string | null;
  phone: string | null;
  mailingAddress: string | null;
}

export interface GroupedPreferencesAndAllergiesItem {
  id: number;
  fullName: string;
  seatPreference: string | null;
  allergiesAndDietaryRestrictions: Record<string, string> | string | null;
}

export interface GroupedPassportEntry {
  passportNumber: string | null;
  expiryDate: string | null;
}

export interface GroupedPassportItem {
  id: number;
  fullName: string;
  dateOfBirth: string | null;
  data: GroupedPassportEntry[];
}

export interface GroupedComfortRelatedDetailsItem {
  id: number;
  fullName: string;
  height: string | null;
  weight: string | null;
  details: string | null;
}

export interface GroupedImportantDateEvent {
  nameEvent: string | null;
  frequency: string | null;
  date: string | null;
}

export interface GroupedImportantDateItem {
  id: number;
  fullName: string;
  events: GroupedImportantDateEvent[];
}

export interface GroupedLoyaltyProgramsItem {
  id: number;
  fullName: string;
  count: number;
  data: Record<string, string>;
}

export interface GroupedTravelDataItem {
  id: number;
  fullName: string;
  globalEntryNumber: string | null;
  globalEntryExpirationDate: string | null;
  knownTravelerNumber: string | null;
  knownTravelerNumberExpirationDate: string | null;
  expirationDate: string | null;
}

export interface ClientGroupedData {
  basicInformation: GroupedBasicInformationItem[];
  contactInformation: GroupedContactInformationItem[];
  preferencesAndAllergies: GroupedPreferencesAndAllergiesItem[];
  passports: GroupedPassportItem[];
  comfortRelatedDetails: GroupedComfortRelatedDetailsItem[];
  importantDates: GroupedImportantDateItem[];
  loyaltyPrograms: GroupedLoyaltyProgramsItem[];
  travelData: GroupedTravelDataItem[];
}

export type GetClientGroupedResponse = ResponseType<ClientGroupedData>;

export type DuplicateMatchField = 'email' | 'name';

export interface DuplicateClientAgency {
  id: number;
  name: string;
}

export interface DuplicateClientAgent {
  id: number;
  name: string;
}

export interface DuplicateClientMatch {
  id: number;
  uuid: string;
  full_name: string;
  email: string;
  profile_picture_url: Nullable<string>;
  match_reasons: DuplicateMatchField[];
  match_label: string;
  agency: Nullable<DuplicateClientAgency>;
  agent: Nullable<DuplicateClientAgent>;
}

export type CheckDuplicateClientsPayload =
  | {
      first_name: string;
      exclude_client_id?: number;
      last_name: string;
      email: string;
    }
  | {
      full_name: string;
      exclude_client_id?: number;
      email: string;
    };

export interface CheckDuplicateClientsResponse {
  success: boolean;
  has_duplicates: boolean;
  duplicates: DuplicateClientMatch[];
}
